import RadioGroup from '@mui/material/RadioGroup';
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

import Button from '../../generic/Button/Button';
import LabeledRadio from '../../generic/Forms/LabeledRadio';

import UseDifferentAccountButton from './UseDifferentAccountButton';

import sharedStyles from '../Login.module.less';
import styles from './OrgSelectForm.module.less';

/** One selectable organization in the picker. `secondaryLabel`/`isDisabled` support the credential signup-suggestion variant (e.g. "Sign in with Google", disabled LDAP). */
export type OrgOption = {
  organizationId: number;
  label: string;
  secondaryLabel?: string;
  isDisabled?: boolean;
};

export type OrgSelectFormProps = {
  legend: string;
  options: OrgOption[];
  /** Called with the chosen org id when the user submits. The parent owns what "pick" does (advance, redirect, or a mutation). */
  onSubmit: (organizationId: number) => void;
  onBack: () => void;
  /** Disables the submit button while an async pick is in flight (used by the social-complete caller). */
  isSubmitting?: boolean;
  /** Inline failure shown above the submit button (used by the social-complete caller). */
  errorMessage?: string | null;
};

/**
 * The presentational organization-select form: a radio list of orgs with a submit + "use a different account" back
 * control. It owns only the selection mechanics (default selection, focus, keyboard) — every caller supplies the
 * option list, the legend copy, and an `onSubmit` that decides what picking an org actually does. Both the credential
 * picker (`OrgPickerStep`) and the social picker (`SocialOrgPickerStep`) render this.
 */
const OrgSelectForm = ({
  legend,
  options,
  onSubmit,
  onBack,
  isSubmitting = false,
  errorMessage = null,
}: OrgSelectFormProps) => {
  // Default to the first selectable option so the user can continue without an extra click (disabled options — e.g.
  // LDAP suggestions — are skipped).
  const defaultOrgId = options.find(option => !option.isDisabled)?.organizationId;
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(defaultOrgId ?? null);
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);

  // Move focus to the first selectable option on mount so keyboard users land in the group without a stray Tab.
  useEffect(() => {
    fieldsetRef.current?.querySelector<HTMLInputElement>('input[type="radio"]:not(:disabled)')?.focus();
  }, []);

  const handleChange = (_event: ChangeEvent<HTMLInputElement>, value: string) => setSelectedOrgId(Number(value));

  const handleSubmit = () => {
    if (selectedOrgId == null || isSubmitting) {
      return;
    }
    onSubmit(selectedOrgId);
  };

  // Radio groups don't trigger implicit form submission, so wire Enter explicitly once an org is selected.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && selectedOrgId != null) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <form
      className={sharedStyles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={sharedStyles.heading}>Choose an organization</h1>
      <fieldset ref={fieldsetRef} className={styles.picker}>
        <legend className={styles.legend}>{legend}</legend>
        <RadioGroup
          name="org-picker"
          value={selectedOrgId == null ? '' : String(selectedOrgId)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        >
          {options.map(option => (
            <LabeledRadio
              key={option.organizationId}
              value={String(option.organizationId)}
              isDisabled={option.isDisabled}
              label={
                option.secondaryLabel ? (
                  <span className={styles.option}>
                    <span>{option.label}</span>
                    <small className={styles.optionSecondary}>{option.secondaryLabel}</small>
                  </span>
                ) : (
                  <span className={styles.option}>{option.label}</span>
                )
              }
            />
          ))}
        </RadioGroup>
      </fieldset>
      {errorMessage && (
        <p role="alert" className={sharedStyles.error}>
          {errorMessage}
        </p>
      )}
      <Button isFullWidth size="large" isDisabled={selectedOrgId == null || isSubmitting} onClick={handleSubmit}>
        Next
      </Button>
      <UseDifferentAccountButton onClick={onBack} />
    </form>
  );
};

export default OrgSelectForm;
